/**
 * Detect content format for rich-text rendering.
 * Returns 'tiptap' | 'markdown' | 'plain'
 */
export function detectRichTextFormat(
    data: unknown
): 'tiptap' | 'markdown' | 'plain' {
    if (data == null) return 'plain';
    if (typeof data === 'string') return looksLikeMarkdown(data) ? 'markdown' : 'plain';
    if (
        typeof data === 'object' &&
        'markdown' in data &&
        typeof (data as { markdown: unknown }).markdown === 'string'
    )
        return 'markdown';
    if (
        typeof data === 'object' &&
        'type' in data &&
        (data as { type?: string }).type === 'doc' &&
        'content' in data
    )
        return 'tiptap';
    return 'plain';
}

/** Heuristic: does the string contain Markdown formatting? */
export function looksLikeMarkdown(text: string): boolean {
    if (!text || typeof text !== 'string') return false;
    const trimmed = text.trim();
    return (
        /\*\*[^*]+\*\*/.test(trimmed) || // **bold**
        /\*[^*]+\*/.test(trimmed) || // *italic*
        /__[^_]+__/.test(trimmed) || // __bold__
        /^#{1,6}\s/m.test(trimmed) || // # headers
        /^- .+/m.test(trimmed) || // - list
        /^\d+\. .+/m.test(trimmed) || // ordered list
        /`[^`]+`/.test(trimmed) || // `code`
        /!\[[^\]]*\]\([^)]+\)/.test(trimmed) || // ![alt](url)
        /\[[^\]]+\]\([^)]+\)/.test(trimmed) // [text](url)
    );
}

/** Extract markdown string from data, or null if not markdown. */
export function getMarkdownString(data: unknown): string | null {
    if (typeof data === 'string') return data;
    if (
        data &&
        typeof data === 'object' &&
        'markdown' in data &&
        typeof (data as { markdown: unknown }).markdown === 'string'
    )
        return (data as { markdown: string }).markdown;
    return null;
}
